import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Button, Alert, TextField,
  InputAdornment, Tabs, Tab, LinearProgress, Select, MenuItem, FormControl,
  InputLabel, Skeleton
} from '@mui/material';
import { RateReview, Search, PlayArrow } from '@mui/icons-material';
import { reviewerAPI } from '../../services/api';
import dayjs from 'dayjs';

function isDone(a) { return a.review_status === 'completed'; }
function isStarted(a) { return a.review_status === 'in_progress'; }

export default function ReviewerDashboard() {
  var as = useState([]); var assignments = as[0]; var setAssignments = as[1];
  var ls = useState(true); var loading = ls[0]; var setLoading = ls[1];
  var qs = useState(''); var query = qs[0]; var setQuery = qs[1];
  var ts = useState('todo'); var tab = ts[0]; var setTab = ts[1];
  var fs = useState('all'); var formFilter = fs[0]; var setFormFilter = fs[1];
  var ss = useState('priority'); var sort = ss[0]; var setSort = ss[1];
  var ps = useState('all'); var tierFilter = ps[0]; var setTierFilter = ps[1];
  var navigate = useNavigate();

  useEffect(function() {
    reviewerAPI.myAssignments()
      .then(function(r) { setAssignments(r.data.data || []); })
      .catch(function() { setAssignments([]); })
      .then(function() { setLoading(false); });
  }, []);

  function labelFor(a) {
    return a.anonymous_review
      ? 'Application #' + (a.application_id || '').substring(0, 8).toUpperCase()
      : (a.applicant_name || 'Unnamed applicant');
  }

  var total = assignments.length;
  var done = assignments.filter(isDone).length;
  var started = assignments.filter(isStarted).length;
  var todo = total - done;
  var pct = total ? Math.round((done / total) * 100) : 0;

  // Distinct forms, for the form filter
  var formTitles = [];
  assignments.forEach(function(a) {
    if (a.form_title && formTitles.indexOf(a.form_title) === -1) formTitles.push(a.form_title);
  });

  // Priority tiers, in the order the server returned them.
  var tiers = [];
  assignments.forEach(function(a) {
    if (a.priority_tier && tiers.indexOf(a.priority_tier) === -1) tiers.push(a.priority_tier);
  });

  var filtered = assignments.filter(function(a) {
    if (tab === 'todo' && isDone(a)) return false;
    if (tab === 'in_progress' && !isStarted(a)) return false;
    if (tab === 'completed' && !isDone(a)) return false;
    if (formFilter !== 'all' && a.form_title !== formFilter) return false;
    if (tierFilter !== 'all' && a.priority_tier !== tierFilter) return false;
    if (query) {
      var hay = (labelFor(a) + ' ' + (a.form_title || '') + ' ' + (a.application_id || '')).toLowerCase();
      if (hay.indexOf(query.toLowerCase()) === -1) return false;
    }
    return true;
  });

  // 'priority' keeps the server's ordering, which already puts the tier that
  // should be reviewed first at the top.
  if (sort !== 'priority') {
    filtered = filtered.slice().sort(function(a, b) {
      if (sort === 'name') return labelFor(a).localeCompare(labelFor(b));
      var da = new Date(a.submitted_at).getTime();
      var db = new Date(b.submitted_at).getTime();
      return sort === 'newest' ? db - da : da - db;
    });
  }

  var nextUp = assignments.filter(function(a) { return !isDone(a); })[0];

  var E = React.createElement;

  if (loading) {
    return E(Box, null,
      E(Typography, {variant:'h4', sx:{mb:3}}, 'My reviews'),
      E(Skeleton, {variant:'rectangular', height:90, sx:{mb:2, borderRadius:2}}),
      E(Skeleton, {variant:'rectangular', height:200, sx:{borderRadius:2}}));
  }

  if (total === 0) {
    return E(Box, null,
      E(Typography, {variant:'h4', sx:{mb:3}}, 'My reviews'),
      E(Card, null, E(CardContent, {sx:{textAlign:'center', py:8}},
        E(Typography, {variant:'h6', sx:{mb:1}}, 'Nothing assigned yet'),
        E(Typography, {color:'text.secondary'},
          'Applications will appear here once an organiser assigns them to you.'))));
  }

  return E(Box, null,
    E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:2, mb:2}},
      E(Typography, {variant:'h4'}, 'My reviews'),
      nextUp ? E(Button, {variant:'contained', startIcon:E(PlayArrow),
        onClick:function(){ navigate('/reviewer/review/' + nextUp.assignment_id); }},
        isStarted(nextUp) ? 'Continue where I left off' : 'Start next review') : null),

    // Progress
    E(Card, {sx:{mb:3}},
      E(CardContent, null,
        E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'baseline', mb:1}},
          E(Typography, {variant:'subtitle1', fontWeight:600},
            done + ' of ' + total + ' done'),
          E(Typography, {variant:'body2', color:'text.secondary'},
            todo === 0 ? 'All caught up' : todo + ' left to review')),
        E(LinearProgress, {variant:'determinate', value:pct, sx:{height:8, borderRadius:4}}))),

    // Filters
    E(Box, {sx:{display:'flex', gap:1.5, flexWrap:'wrap', alignItems:'center', mb:2}},
      E(TextField, {size:'small', placeholder:'Search by name, ID or form', value:query,
        onChange:function(e){ setQuery(e.target.value); },
        sx:{flex:'1 1 260px', maxWidth:400},
        InputProps:{startAdornment:E(InputAdornment, {position:'start'}, E(Search, {fontSize:'small'}))}}),
      formTitles.length > 1 ? E(FormControl, {size:'small', sx:{minWidth:180}},
        E(InputLabel, null, 'Form'),
        E(Select, {value:formFilter, label:'Form', onChange:function(e){ setFormFilter(e.target.value); }},
          E(MenuItem, {value:'all'}, 'All forms'),
          formTitles.map(function(t){ return E(MenuItem, {key:t, value:t}, t); }))) : null,
      tiers.length > 1 ? E(FormControl, {size:'small', sx:{minWidth:170}},
        E(InputLabel, null, 'Priority'),
        E(Select, {value:tierFilter, label:'Priority', onChange:function(e){ setTierFilter(e.target.value); }},
          E(MenuItem, {value:'all'}, 'All groups'),
          tiers.map(function(t){ return E(MenuItem, {key:t, value:t}, t); }))) : null,
      E(FormControl, {size:'small', sx:{minWidth:160}},
        E(InputLabel, null, 'Sort by'),
        E(Select, {value:sort, label:'Sort by', onChange:function(e){ setSort(e.target.value); }},
          E(MenuItem, {value:'priority'}, 'Review order'),
          E(MenuItem, {value:'oldest'}, 'Oldest first'),
          E(MenuItem, {value:'newest'}, 'Newest first'),
          E(MenuItem, {value:'name'}, 'Name A\u2013Z')))),

    E(Tabs, {value:tab, onChange:function(_, v){ setTab(v); },
      variant:'scrollable', scrollButtons:'auto',
      sx:{mb:2, borderBottom:1, borderColor:'divider'}},
      E(Tab, {value:'todo', label:'To review (' + todo + ')'}),
      E(Tab, {value:'in_progress', label:'In progress (' + started + ')'}),
      E(Tab, {value:'completed', label:'Done (' + done + ')'}),
      E(Tab, {value:'all', label:'All (' + total + ')'})),

    filtered.length === 0 ? E(Alert, {severity:'info'},
      query ? 'Nothing matches "' + query + '".' : 'Nothing in this list.') : null,

    E(Grid, {container:true, spacing:2},
      filtered.map(function(a) {
        var color = isDone(a) ? 'success.main' : isStarted(a) ? 'warning.main' : 'primary.main';
        return E(Grid, {item:true, xs:12, md:6, key:a.assignment_id},
          E(Card, {sx:{borderLeft:4, borderColor:color, height:'100%'}},
            E(CardContent, null,
              E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:1, mb:1}},
                E(Typography, {variant:'h6', sx:{wordBreak:'break-word'}}, labelFor(a)),
                E(Chip, {label:a.review_status.replace('_', ' '), size:'small',
                  color:isDone(a) ? 'success' : isStarted(a) ? 'warning' : 'default'})),
              E(Box, {sx:{display:'flex', gap:0.5, flexWrap:'wrap', mb:1}},
                a.priority_tier ? E(Chip, {label:a.priority_tier, size:'small', variant:'outlined',
                  color: a.priority_rank === 0 ? 'primary' : 'default'}) : null),
              E(Typography, {variant:'body2', color:'text.secondary', sx:{mb:1.5}},
                a.form_title + ' \u2022 Submitted ' + dayjs(a.submitted_at).format('MMM D, YYYY')),
              E(Button, {
                variant: isDone(a) ? 'outlined' : 'contained', size:'small',
                startIcon:E(RateReview),
                onClick:function(){ navigate('/reviewer/review/' + a.assignment_id); }},
                isDone(a) ? 'View review' : isStarted(a) ? 'Continue review' : 'Start review'))));
      })));
}
